/**
 * The keyboard system: single keys, modifier combos and Linear-style sequences
 * (`g` then `i`), plus a scope stack so an open dialog silences the app behind
 * it without every screen having to check `isDialogOpen`.
 *
 * One `keydown` listener serves the whole app. Registrations are matched most
 * recent first, so a locally mounted binding beats a global one.
 */

import { useEffect, useRef } from 'react';

export const IS_MAC =
  typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

/* ────────────────────────────────────────────────────────────────────────────
   Spec parsing
   ──────────────────────────────────────────────────────────────────────────── */

interface Chord {
  key: string;
  ctrl: boolean;
  meta: boolean;
  alt: boolean;
  shift: boolean;
  /** Whether the spec said anything about shift at all. */
  shiftDeclared: boolean;
}

const KEY_ALIASES: Record<string, string> = {
  esc: 'escape',
  return: 'enter',
  space: ' ',
  spacebar: ' ',
  up: 'arrowup',
  down: 'arrowdown',
  left: 'arrowleft',
  right: 'arrowright',
  del: 'delete',
  ins: 'insert',
  pgup: 'pageup',
  pgdn: 'pagedown',
  period: '.',
  dot: '.',
  comma: ',',
  slash: '/',
  backslash: '\\',
  minus: '-',
  plus: '+',
  equal: '=',
};

function parseChord(token: string): Chord {
  const chord: Chord = {
    key: '',
    ctrl: false,
    meta: false,
    alt: false,
    shift: false,
    shiftDeclared: false,
  };

  // `+` is both a separator and a key; only split on separators that have a neighbour.
  const parts = token.split('+').reduce<string[]>((acc, part, index, all) => {
    if (part === '' && index > 0 && index < all.length - 1) return acc; // "mod++" → key "+"
    acc.push(part === '' ? '+' : part);
    return acc;
  }, []);

  for (let i = 0; i < parts.length; i += 1) {
    const raw = parts[i]!.toLowerCase();
    const isLast = i === parts.length - 1;

    if (!isLast || parts.length === 1) {
      switch (raw) {
        case 'mod':
          if (IS_MAC) chord.meta = true;
          else chord.ctrl = true;
          continue;
        case 'cmd':
        case 'command':
        case 'meta':
        case 'super':
          chord.meta = true;
          continue;
        case 'ctrl':
        case 'control':
          chord.ctrl = true;
          continue;
        case 'alt':
        case 'option':
        case 'opt':
          chord.alt = true;
          continue;
        case 'shift':
          chord.shift = true;
          chord.shiftDeclared = true;
          continue;
        default:
          break;
      }
    }
    chord.key = KEY_ALIASES[raw] ?? raw;
  }

  return chord;
}

/** `'g i'` → two chords; `'mod+k'` → one. */
export function parseHotkey(spec: string): Chord[] {
  return spec
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(parseChord);
}

const ALPHANUMERIC = /^[a-z0-9]$/;

function eventMatches(event: KeyboardEvent, chord: Chord): boolean {
  if (event.key.toLowerCase() !== chord.key) return false;
  if (event.ctrlKey !== chord.ctrl) return false;
  if (event.metaKey !== chord.meta) return false;
  if (event.altKey !== chord.alt) return false;

  // `?` and `:` already imply Shift on most layouts — only enforce it when the
  // spec asked for it, or when the key is one Shift does not transform.
  if (chord.shiftDeclared || ALPHANUMERIC.test(chord.key)) {
    if (event.shiftKey !== chord.shift) return false;
  }
  return true;
}

/* ────────────────────────────────────────────────────────────────────────────
   Scope stack
   ──────────────────────────────────────────────────────────────────────────── */

export const GLOBAL_SCOPE = 'global';
/** A binding registered with this scope fires no matter what is on top. */
export const ANY_SCOPE = '*';

const scopeStack: string[] = [];

export function activeHotkeyScope(): string {
  return scopeStack[scopeStack.length - 1] ?? GLOBAL_SCOPE;
}

/** Push a scope; returns the matching pop. Overlays call this on open. */
export function pushHotkeyScope(id: string): () => void {
  scopeStack.push(id);
  let released = false;
  return () => {
    if (released) return;
    released = true;
    const index = scopeStack.lastIndexOf(id);
    if (index !== -1) scopeStack.splice(index, 1);
  };
}

/** Declarative form: while this component is mounted, `id` owns the keyboard. */
export function useHotkeyScope(id: string, enabled = true): void {
  useEffect(() => {
    if (!enabled) return;
    return pushHotkeyScope(id);
  }, [id, enabled]);
}

/* ────────────────────────────────────────────────────────────────────────────
   Registry & dispatch
   ──────────────────────────────────────────────────────────────────────────── */

export type HotkeySpec = string | string[];
export type HotkeyHandler = (event: KeyboardEvent) => void;

export interface HotkeyOptions {
  enabled?: boolean;
  /** Fires only when this scope is on top. `'*'` ignores the stack. */
  scope?: string;
  /** Fire even when focus is in an input, textarea or contenteditable. */
  allowInInput?: boolean;
  /** Defaults to true — a bound key should not also scroll the page. */
  preventDefault?: boolean;
  /** Auto-repeat while held. Defaults to true for plain keys (`j`/`k`). */
  repeat?: boolean;
  /** Let other registrations for the same chord run too. */
  passthrough?: boolean;
}

interface Registration {
  order: number;
  specs: Chord[][];
  handler: { current: HotkeyHandler };
  options: Required<Omit<HotkeyOptions, 'repeat'>> & { repeat: boolean | undefined };
}

let registrationSeq = 0;
const registrations = new Set<Registration>();
let listenerAttached = false;

/** Buffer for multi-key sequences; cleared after a pause or a non-match. */
const SEQUENCE_TIMEOUT_MS = 900;
let sequenceBuffer: KeyboardEvent[] = [];
let sequenceTimer: number | undefined;

function resetSequence(): void {
  sequenceBuffer = [];
  if (sequenceTimer !== undefined) {
    window.clearTimeout(sequenceTimer);
    sequenceTimer = undefined;
  }
}

function armSequence(): void {
  if (sequenceTimer !== undefined) window.clearTimeout(sequenceTimer);
  sequenceTimer = window.setTimeout(resetSequence, SEQUENCE_TIMEOUT_MS);
}

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

export function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (EDITABLE_TAGS.has(target.tagName)) return true;
  if (target.isContentEditable) return true;
  return target.closest('[contenteditable="true"]') !== null;
}

function scopeAllows(scope: string): boolean {
  return scope === ANY_SCOPE || scope === activeHotkeyScope();
}

function hasModifier(chord: Chord): boolean {
  return chord.ctrl || chord.meta || chord.alt;
}

function allowsEvent(registration: Registration, event: KeyboardEvent, chord: Chord): boolean {
  if (!registration.options.enabled) return false;
  if (!scopeAllows(registration.options.scope)) return false;

  if (event.repeat) {
    const repeat = registration.options.repeat ?? !hasModifier(chord);
    if (!repeat) return false;
  }

  if (isEditableTarget(event.target) && !registration.options.allowInInput) {
    // Modifier combos and Escape still belong to the app while typing.
    if (!hasModifier(chord) && chord.key !== 'escape') return false;
  }
  return true;
}

function run(registration: Registration, event: KeyboardEvent): boolean {
  if (registration.options.preventDefault) event.preventDefault();
  registration.handler.current(event);
  return !registration.options.passthrough;
}

/** Every registered chord list, newest registration first. */
function orderedRegistrations(): Registration[] {
  return [...registrations].sort((a, b) => b.order - a.order);
}

function matchTail(events: KeyboardEvent[], chords: Chord[]): boolean {
  if (chords.length > events.length) return false;
  const offset = events.length - chords.length;
  return chords.every((chord, i) => eventMatches(events[offset + i]!, chord));
}

function isSequencePrefix(events: KeyboardEvent[], chords: Chord[]): boolean {
  if (chords.length <= events.length) return false;
  return events.every((event, i) => eventMatches(event, chords[i]!));
}

function onKeyDown(event: KeyboardEvent): void {
  if (event.defaultPrevented) return;
  const key = event.key;
  if (key === 'Shift' || key === 'Control' || key === 'Alt' || key === 'Meta') return;

  const ordered = orderedRegistrations();
  const hadBuffer = sequenceBuffer.length > 0;
  const candidate = [...sequenceBuffer, event];

  // 1. A sequence that just completed wins over any single-key binding.
  for (const registration of ordered) {
    for (const chords of registration.specs) {
      if (chords.length < 2) continue;
      if (!matchTail(candidate, chords)) continue;
      if (!allowsEvent(registration, event, chords[chords.length - 1]!)) continue;
      resetSequence();
      if (run(registration, event)) return;
    }
  }

  // 2. Is this the start (or middle) of one? Hold the key back.
  if (bufferIfPrefix(ordered, candidate, event)) return;

  // 3. A sequence was in flight and this key did not continue it. Abort it and
  //    swallow the key — after `g`, an unmapped key must not fire its own
  //    binding, or a mistyped `g x` would silently select a row.
  if (hadBuffer) {
    resetSequence();
    bufferIfPrefix(ordered, [event], event);
    return;
  }

  resetSequence();

  // 4. Plain chords.
  for (const registration of ordered) {
    for (const chords of registration.specs) {
      if (chords.length !== 1) continue;
      const chord = chords[0]!;
      if (!eventMatches(event, chord)) continue;
      if (!allowsEvent(registration, event, chord)) continue;
      if (run(registration, event)) return;
    }
  }
}

function bufferIfPrefix(
  ordered: Registration[],
  candidate: KeyboardEvent[],
  event: KeyboardEvent,
): boolean {
  const startsSequence = ordered.some((registration) =>
    registration.specs.some(
      (chords) =>
        chords.length >= 2 &&
        isSequencePrefix(candidate, chords) &&
        registration.options.enabled &&
        scopeAllows(registration.options.scope) &&
        !(isEditableTarget(event.target) && !registration.options.allowInInput),
    ),
  );
  if (!startsSequence) return false;
  sequenceBuffer = candidate;
  armSequence();
  return true;
}

function ensureListener(): void {
  if (listenerAttached || typeof window === 'undefined') return;
  window.addEventListener('keydown', onKeyDown);
  listenerAttached = true;
}

/**
 * Imperative binding, for code that is not a component. Returns the unbind.
 * `useHotkey` is a thin wrapper around this.
 */
export function registerHotkey(
  spec: HotkeySpec,
  handler: HotkeyHandler,
  options: HotkeyOptions = {},
): () => void {
  ensureListener();
  registrationSeq += 1;

  const registration: Registration = {
    order: registrationSeq,
    specs: (Array.isArray(spec) ? spec : [spec]).filter(Boolean).map(parseHotkey),
    handler: { current: handler },
    options: {
      enabled: options.enabled ?? true,
      scope: options.scope ?? GLOBAL_SCOPE,
      allowInInput: options.allowInInput ?? false,
      preventDefault: options.preventDefault ?? true,
      repeat: options.repeat,
      passthrough: options.passthrough ?? false,
    },
  };
  registrations.add(registration);
  return () => {
    registrations.delete(registration);
  };
}

/**
 * Bind one or more specs to a handler for as long as the component is mounted.
 * The handler is read through a ref, so it never needs to be memoised.
 */
export function useHotkey(spec: HotkeySpec, handler: HotkeyHandler, options: HotkeyOptions = {}): void {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  const {
    enabled = true,
    scope = GLOBAL_SCOPE,
    allowInInput = false,
    preventDefault = true,
    repeat,
    passthrough = false,
  } = options;

  const specKey = Array.isArray(spec) ? spec.join('|') : spec;

  useEffect(() => {
    if (!enabled) return;
    return registerHotkey(
      specKey.split('|'),
      (event) => handlerRef.current(event),
      { enabled, scope, allowInInput, preventDefault, repeat, passthrough },
    );
  }, [specKey, enabled, scope, allowInInput, preventDefault, repeat, passthrough]);
}

/* ────────────────────────────────────────────────────────────────────────────
   Display
   ──────────────────────────────────────────────────────────────────────────── */

const DISPLAY_KEYS: Record<string, string> = {
  escape: 'Esc',
  enter: '↵',
  arrowup: '↑',
  arrowdown: '↓',
  arrowleft: '←',
  arrowright: '→',
  backspace: '⌫',
  delete: 'Del',
  tab: '⇥',
  ' ': 'Space',
};

/** Chips for `<Kbd>` — OS-correct and already capitalised. */
export function formatKeys(spec: string): string[] {
  const chips: string[] = [];
  for (const chord of parseHotkey(spec)) {
    const parts: string[] = [];
    if (chord.meta) parts.push(IS_MAC ? '⌘' : 'Win');
    if (chord.ctrl) parts.push(IS_MAC ? '⌃' : 'Ctrl');
    if (chord.alt) parts.push(IS_MAC ? '⌥' : 'Alt');
    if (chord.shiftDeclared && chord.shift) parts.push(IS_MAC ? '⇧' : 'Shift');
    parts.push(
      DISPLAY_KEYS[chord.key] ?? (chord.key.length === 1 ? chord.key.toUpperCase() : chord.key),
    );
    chips.push(parts.join(IS_MAC ? '' : '+'));
  }
  return chips;
}

/* ────────────────────────────────────────────────────────────────────────────
   The canonical keymap (build contract §7)
   ──────────────────────────────────────────────────────────────────────────── */

export type ShortcutGroup = 'General' | 'Navigation' | 'List' | 'Record';

export type ShortcutId =
  | 'palette'
  | 'search'
  | 'help'
  | 'toggleSidebar'
  | 'toggleDetail'
  | 'toggleTheme'
  | 'goInbox'
  | 'goBoard'
  | 'goContacts'
  | 'goAnalytics'
  | 'goTasks'
  | 'goSettings'
  | 'nextRow'
  | 'prevRow'
  | 'toggleSelect'
  | 'extendSelect'
  | 'rowMenu'
  | 'completeTask'
  | 'openRecord'
  | 'closeOverlay'
  | 'archive'
  | 'assign'
  | 'stage'
  | 'tag'
  | 'note'
  | 'reply'
  | 'focusRecord';

export interface Shortcut {
  id: ShortcutId;
  /** Bind with this. */
  spec: string;
  /** Render this — already resolved for the current OS. */
  keys: string[];
  label: string;
  group: ShortcutGroup;
  /** Two-step key sequence, rendered as "G then I". */
  sequence: boolean;
  /** Context the action needs; the sheet greys out what does not apply. */
  requires?: 'list' | 'record';
}

function define(
  id: ShortcutId,
  spec: string,
  label: string,
  group: ShortcutGroup,
  requires?: Shortcut['requires'],
): Shortcut {
  const keys = formatKeys(spec);
  return { id, spec, keys, label, group, sequence: keys.length > 1, requires };
}

/**
 * The single source of truth for the keymap. The command palette, the `?`
 * sheet and every `useHotkey` call read from here — nothing hard-codes a key.
 */
export const SHORTCUTS: Shortcut[] = [
  define('palette', 'mod+k', 'Open command palette', 'General'),
  define('search', '/', 'Focus search', 'General'),
  define('help', '?', 'Keyboard shortcuts', 'General'),
  define('toggleSidebar', 'mod+\\', 'Toggle sidebar', 'General'),
  define('toggleDetail', 'mod+.', 'Toggle detail pane', 'General'),
  define('toggleTheme', 'mod+shift+l', 'Toggle light / dark', 'General'),

  define('goInbox', 'g i', 'Go to Inbox', 'Navigation'),
  define('goBoard', 'g b', 'Go to Board', 'Navigation'),
  define('goContacts', 'g c', 'Go to Contacts', 'Navigation'),
  define('goAnalytics', 'g a', 'Go to Analytics', 'Navigation'),
  define('goTasks', 'g t', 'Go to Tasks', 'Navigation'),
  define('goSettings', 'g s', 'Go to Settings', 'Navigation'),

  define('nextRow', 'j', 'Next row', 'List', 'list'),
  define('prevRow', 'k', 'Previous row', 'List', 'list'),
  /*
   * `X` MEANS SELECT, ON EVERY LIST.
   *
   * This entry is the product's global reference for the key — it is what the
   * `?` sheet prints — and `/tasks` used to bind it to *completion*: pressing X
   * on a focused task row ticked the task, raised "Task completed" and removed
   * the row from the open scope, while the same key on `/inbox` toggled a
   * non-destructive selection. One key, two documents, and the mutating one was
   * undocumented. Completion has its own key below; nothing else may take this
   * one. `scripts/invariants-check.mjs` walks every `list`-scope binding here,
   * presses it on every list surface and asserts the observed effect matches
   * this label.
   */
  define('toggleSelect', 'x', 'Select row', 'List', 'list'),
  define('extendSelect', 'shift+arrowdown', 'Extend the selection', 'List', 'list'),
  /*
   * The row's own actions. `.` is what Linear and GitHub both use; the platform
   * contract for a context menu on a focused element — `Shift+F10` and the
   * `ContextMenu` key — opens the same panel and is bound beside it, which is
   * why the sheet prints the letter rather than three of them.
   */
  define('rowMenu', '.', 'Row actions', 'List', 'list'),
  /*
   * Completion, which is a *write*, so it does not get to share a key with a
   * selection toggle. `D` for done: unused anywhere else in the registry, a
   * single letter like every other key in this group, and it is the key the row
   * menu's own "Mark complete" item now advertises.
   */
  define('completeTask', 'd', 'Complete task', 'List', 'list'),
  define('openRecord', 'enter', 'Open record', 'List', 'list'),
  define('closeOverlay', 'escape', 'Close overlay or clear selection', 'List'),

  define('archive', 'e', 'Archive', 'Record', 'record'),
  define('assign', 'a', 'Assign owner', 'Record', 'record'),
  define('stage', 's', 'Change stage', 'Record', 'record'),
  define('tag', 'l', 'Edit tags', 'Record', 'record'),
  define('note', 'n', 'Add note', 'Record', 'record'),
  /*
   * The product's primary verb had no key at all, and the record pane had no
   * keyboard entry point: the first focusable inside it sat 171 tab stops from
   * the top of the screen, behind 50 list rows. `F` moves focus into the open
   * record; `R` puts the cursor straight in the reply field.
   *
   * Neither is a modifier chord on purpose — every other record action here is a
   * single letter — and neither collides: `shift+enter` would have matched the
   * `enter`/openRecord chord too, because Shift is only enforced on keys Shift
   * transforms.
   */
  define('reply', 'r', 'Reply', 'Record', 'record'),
  define('focusRecord', 'f', 'Focus the open record', 'Record', 'record'),
];

export const SHORTCUTS_BY_ID: Record<ShortcutId, Shortcut> = Object.fromEntries(
  SHORTCUTS.map((s) => [s.id, s]),
) as Record<ShortcutId, Shortcut>;

export const SHORTCUT_GROUP_ORDER: ShortcutGroup[] = ['General', 'Navigation', 'List', 'Record'];

/** `useHotkey(shortcutSpec('palette'), …)` — never retype a key string. */
export function shortcutSpec(id: ShortcutId): string {
  return SHORTCUTS_BY_ID[id].spec;
}

/** Bind a registry entry by id; the label and keys stay in one place. */
export function useShortcut(id: ShortcutId, handler: HotkeyHandler, options?: HotkeyOptions): void {
  useHotkey(SHORTCUTS_BY_ID[id].spec, handler, options);
}
